// requip: src/components/Chrome.jsx

import React, { forwardRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { ArrowUp, Menu, Moon, Phone, Sun, X } from "lucide-react";
import { useTheme } from "../theme";
import { Magnetic } from "./Effects";
import { SITE } from "../content";
import { useContent } from "../ContentContext";

// Page anchors live on the catalogue, so from an item page they have to
// route home first. NavLink below handles that.
const NAV = [
  { label: "In stock", href: "/#stock" },
  { label: "How it works", href: "/#how" },
];

// Shared by the header and the back-to-top button so they appear together.
function useScrolledPast(px) {
  const [past, setPast] = useState(false);

  useEffect(() => {
    const onScroll = () => setPast(window.scrollY > px);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [px]);

  return past;
}

// An <a> for accessibility and middle-click, routed in-app on a normal click
// so the catalogue does not reload. App.js scrolls to the hash once the
// target exists.
const NavLink = forwardRef(function NavLink({ href, onClick, children, ...rest }, ref) {
  const navigate = useNavigate();
  return (
    <a
      ref={ref}
      href={href}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(href);
      }}
      {...rest}
    >
      {children}
    </a>
  );
});

const MotionNavLink = motion.create(NavLink);

function Logo({ className = "h-8" }) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/logo512.png`}
      alt="Brooke Refrigeration"
      className={`${className} w-auto`}
    />
  );
}

function ThemeButton() {
  const { theme, toggle } = useTheme();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="grid h-10 w-10 place-items-center rounded-full border border-line text-muted transition hover:border-brooke-red hover:text-ink"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ opacity: 0, rotate: -60, scale: 0.7 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 60, scale: 0.7 }}
          transition={{ duration: 0.22 }}
          className="grid place-items-center"
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

// ---------------------------------------------------------------------------
export function Header() {
  const solid = useScrolledPast(40);
  const [open, setOpen] = useState(false);

  // The sheet covers the page, so the body must not scroll behind it.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          solid ? "border-b border-line bg-bg/90 backdrop-blur" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <a href={SITE.gateway} className="group flex items-center gap-3">
            <Logo />
            <span className="hidden font-mono text-[9px] uppercase tracking-[0.22em] text-subtle transition group-hover:text-brooke-red sm:block">
              Requip
            </span>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                href={n.href}
                className="relative font-mono text-[10px] uppercase tracking-[0.16em] text-muted transition after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brooke-red after:transition-all after:duration-300 hover:text-ink hover:after:w-full"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeButton />

            <a
              href="#enquire"
              className="hidden rounded-sm border border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted transition hover:border-brooke-red hover:bg-brooke-red/10 hover:text-ink sm:block"
            >
              Enquire
            </a>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="grid h-10 w-10 place-items-center rounded-full border border-line text-muted transition hover:border-brooke-red hover:text-ink md:hidden"
            >
              <Menu size={16} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Full-height sheet. Links stagger in, so the panel has some life without
// costing anything - it is opacity and transform only.
// ---------------------------------------------------------------------------
function MobileMenu({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] bg-bg md:hidden"
        >
          <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-40" />

          <div className="relative flex items-center justify-between px-5 py-4">
            <Logo />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="grid h-10 w-10 place-items-center rounded-full border border-line text-muted"
            >
              <X size={16} />
            </button>
          </div>

          <nav className="relative mt-6 px-5">
            {NAV.map((n, i) => (
              <MotionNavLink
                key={n.href}
                href={n.href}
                onClick={onClose}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 + i * 0.06, duration: 0.35 }}
                className="flex items-baseline justify-between border-b border-line py-5"
              >
                <span className="font-arch text-2xl font-extrabold tracking-tight text-ink">
                  {n.label}
                </span>
                <span className="font-mono text-[10px] text-subtle">
                  0{i + 1}
                </span>
              </MotionNavLink>
            ))}

            <motion.a
              href="#enquire"
              onClick={onClose}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06 + NAV.length * 0.06, duration: 0.35 }}
              className="mt-8 flex min-h-[52px] items-center justify-center rounded-full bg-brooke-red font-mono text-[10px] uppercase tracking-[0.18em] text-white"
            >
              Start an enquiry
            </motion.a>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Sticky action bar on phones. A refrigeration failure is a phone call, not a
// form, so calling gets equal weight and both stay reachable at any scroll
// position.
// ---------------------------------------------------------------------------
export function MobileBar() {
  const { contact } = useContent();
  const show = useScrolledPast(400);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80 }}
          animate={{ y: 0 }}
          exit={{ y: 80 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-bg/95 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur md:hidden"
        >
          <div className="flex gap-3">
            <a
              href={`tel:${contact.phone}`}
              className="flex min-h-[50px] flex-1 items-center justify-center gap-2 rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.16em] text-ink"
            >
              <Phone size={14} />
              Call
            </a>
            <a
              href="#enquire"
              className="flex min-h-[50px] flex-[1.4] items-center justify-center rounded-full bg-brooke-red font-mono text-[10px] uppercase tracking-[0.16em] text-white"
            >
              Send an enquiry
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
export function BackToTop() {
  const show = useScrolledPast(600);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25 }}
          // clears the mobile bar on phones
          className="fixed bottom-24 right-5 z-40 md:bottom-6 md:right-6"
        >
          <Magnetic strength={0.35}>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
              className="grid h-12 w-12 place-items-center rounded-full border border-line bg-surface/90 text-muted shadow-lg backdrop-blur transition hover:border-brooke-red hover:text-ink"
            >
              <ArrowUp size={17} />
            </button>
          </Magnetic>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
export function Footer() {
  const { contact } = useContent();

  return (
    <footer className="border-t border-line py-12 pb-28 md:pb-12">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-5 sm:px-8 md:flex-row md:items-start md:justify-between">
        <div>
          <Logo className="h-7" />
          <p className="mt-3 max-w-[38ch] text-[13px] leading-relaxed text-subtle">
            Commercial refrigeration since 2000. {contact.street},{" "}
            {contact.locality}, {contact.region} {contact.postal}.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
            Other divisions
          </p>
          <div className="flex flex-wrap gap-5">
            <a
              href={SITE.gateway}
              className="border-b border-transparent text-[13px] text-muted transition hover:border-brooke-red hover:text-ink"
            >
              All divisions
            </a>
            {SITE.siblings.map((s) => (
              <a
                key={s.href}
                href={s.href}
                className="border-b border-transparent text-[13px] text-muted transition hover:border-brooke-red hover:text-ink"
              >
                {s.label}
              </a>
            ))}
          </div>
          <a
            href={`tel:${contact.phone}`}
            className="mt-2 font-mono text-[12px] tracking-[0.06em] text-muted transition hover:text-brooke-red"
          >
            {contact.phoneLabel}
          </a>
        </div>
      </div>

      {/* The small print: whose site it is, and who built it */}
      <div className="mx-auto mt-10 flex max-w-6xl flex-col gap-2 border-t border-line px-5 pt-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <p className="text-[12px] text-subtle">&copy; {new Date().getFullYear()} Brooke Refrigeration</p>
        <p className="text-[12px] text-subtle">
          Developed by{" "}
          <a
            href="https://codebooth.co.za"
            target="_blank"
            rel="noopener"
            className="border-b border-transparent text-muted transition hover:border-brooke-red hover:text-ink"
          >
            CodeBooth
          </a>
        </p>
      </div>
    </footer>
  );
}